// src/components/resources/calculators/CPMCalculator.tsx

'use client'

import { useState, useMemo, useCallback } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import { FAQ } from '~/components/shared/FAQ'
import { Tooltip } from '~/components/Tooltip'
import { formatNumber } from '~/lib/utils/formatters'
import { formatNumberInput, parseInputNumber } from '~/lib/utils/format/input'
import { EmbeddedEmailCapture } from '~/components/forms/variants/embedded/EmbeddedEmailCapture'
import type {
  EmailTemplateConfig,
  AnalyticsConfig,
  CRMConfig
} from '~/components/forms/base/types'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
import { calculateMetrics, type CalculatorInputs, type CalculatorResults } from './CPMCalculator.logic'

interface NumberFormatOptions {
  /**
   * Options for number formatting with units and decimal places.
   * 
   * @typedef {Object} NumberFormatOptions
   * @property {number} [decimals=0] - Number of decimal places to display
   * @property {string} [unit] - Unit to display with the number (e.g., '$', '%')
   * @property {'before' | 'after'} [unitPosition='after'] - Whether to show unit before or after the number
   * @property {boolean} [unitSpace=true] - Whether to include a space between the number and unit
   * @property {number} [minimumFractionDigits] - Minimum number of fraction digits to display
   * @property {number} [maximumFractionDigits] - Maximum number of fraction digits to display
   */
  decimals?: number
  unit?: string
  unitPosition?: 'before' | 'after'
  unitSpace?: boolean
  minimumFractionDigits?: number
  maximumFractionDigits?: number
}

/**
 * Formats a number with optional unit and decimal places.
 * 
 * This function extends the base formatNumber utility by adding support for
 * units and controlling their placement relative to the number.
 * 
 * @param {number} value - The number to format
 * @param {NumberFormatOptions} [options={}] - Formatting options
 *   @param {number} [options.decimals=0] - Number of decimal places
 *   @param {string} [options.unit] - Unit symbol to append/prepend
 *   @param {'before' | 'after'} [options.unitPosition='after'] - Position of the unit
 *   @param {boolean} [options.unitSpace=true] - Whether to add space between number and unit
 * @returns {string} Formatted number string with optional unit
 * @example
 * // Returns "$1,234.56"
 * formatNumberWithOptions(1234.56, { decimals: 2, unit: '$', unitPosition: 'before', unitSpace: false })
 * 
 * // Returns "42.0 %"
 * formatNumberWithOptions(42, { decimals: 1, unit: '%' })
 */
function formatNumberWithOptions(value: number, options: NumberFormatOptions = {}): string {
  const { decimals = 0, unit, unitPosition = 'after', unitSpace = true } = options
  const formattedValue = formatNumber(value, decimals)
  
  if (!unit) return formattedValue
  
  const space = unitSpace ? ' ' : ''
  return unitPosition === 'before'
    ? `${unit}${space}${formattedValue}`
    : `${formattedValue}${space}${unit}`
}

/**
 * Input data for the CPM calculator.
 * 
 * @typedef {Object} CalculatorInputs
 * @property {number} adSpend - Total amount spent on advertising
 * @property {number} impressions - Total number of ad impressions
 * @property {number} [clicks] - Optional number of clicks received
 * @property {number} [conversions] - Optional number of conversions achieved
 */

interface NumberInputProps {
  id: string
  value: string
  onChange: (value: string) => void
  isDecimal?: boolean
  placeholder: string
  label: string
  tooltip: string
  onIncrement: () => void
  onDecrement: () => void
}

const FAQ_ITEMS = [
  {
    question: "What is CPM (Cost Per Mille)?",
    answer: "CPM (Cost Per Mille) is a digital advertising metric that measures the cost per 1,000 impressions. It helps advertisers understand how much they're paying to reach their audience and is particularly useful for brand awareness campaigns. Unlike performance-based metrics like CPC or CPA, CPM focuses on exposure rather than direct actions, making it useful for upper-funnel marketing objectives."
  },
  {
    question: "How is CPM calculated?",
    answer: "CPM is calculated by dividing the total cost of your ad campaign by the number of impressions, then multiplying by 1,000. The formula is: CPM = (Total Ad Spend / Total Impressions) × 1,000. For example, if you spent $500 on a campaign that generated 250,000 impressions, your CPM would be ($500 / 250,000) × 1,000 = $2.00."
  },
  {
    question: "What's a good CPM rate?",
    answer: "A good CPM rate varies significantly by industry, platform, audience targeting, and campaign objectives. Generally, $1-$2 is considered very good, $2-$5 is average, and $5+ may be acceptable for highly targeted campaigns or premium placements. B2B campaigns typically have higher CPMs ($6-$15) than B2C ($2-$8). Premium inventory like connected TV can range from $20-$50 CPM, while programmatic display might average $2-$4 CPM."
  },
  {
    question: "How can I optimize my CPM?",
    answer: "To optimize CPM: 1) Refine your audience targeting to reduce wasted impressions, 2) Test different ad placements and formats to find cost-efficient inventory, 3) Improve ad relevance and quality scores through better creative and messaging, 4) Optimize ad scheduling to avoid high-competition time periods, 5) Consider seasonal trends and competition levels when planning campaigns, and 6) Implement frequency capping to prevent overexposure to the same users."
  },
  {
    question: "When should I focus on CPM vs. other metrics?",
    answer: "Focus on CPM when your primary goal is brand awareness, reach, or visibility. It's most valuable for upper-funnel marketing activities where immediate conversion isn't expected. For performance-driven campaigns, metrics like CPC (Cost Per Click), CPA (Cost Per Acquisition), or ROAS (Return on Ad Spend) are more relevant. The best approach is often to track CPM alongside engagement and conversion metrics to understand both efficiency and effectiveness."
  }
]

const PLATFORM_BENCHMARKS = [
  {
    platform: 'Meta Ads',
    cpm: {
      low: 4.85,
      average: 7.43,
      high: 15.23
    },
    source: 'Meta Ads Transparency Center - Q4 2023',
    url: 'https://www.facebook.com/ads/library/report'
  },
  {
    platform: 'TikTok (Q4 2024)',
    cpm: {
      low: 3.11,
      average: 5.34,
      high: 13.17
    },
    source: 'Gupta Media Interactive CPM Tracker',
    url: 'https://lookerstudio.google.com/u/0/reporting/44d0af1a-8eed-4050-b49a-fe2f508dc68c/page/p_asnhjd8v3c'
  },
  {
    platform: 'YouTube (2024)',
    cpm: {
      low: 2.90,
      average: 3.53,
      high: 4.80
    },
    source: 'Megadigital (2024)',
    url: 'https://megadigital.ai/en/blog/youtube-ad-benchmarks/'
  },
  {
    platform: 'LinkedIn (Q4 2024)',
    cpm: {
      low: 7.96,
      average: 27.38,
      high: 51.95
    },
    source: 'Gupta Media Interactive CPM Tracker',
    url: 'https://lookerstudio.google.com/u/0/reporting/44d0af1a-8eed-4050-b49a-fe2f508dc68c/page/p_v3aq8o8v3c'
  }
]

// Custom input component with better number formatting
const NumberInput = ({ 
  id,
  value,
  onChange,
  isDecimal = false,
  placeholder,
  label,
  tooltip,
  onIncrement,
  onDecrement
}: NumberInputProps): JSX.Element => (
  <div>
    <div className="flex items-center justify-between">
      <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
        {label}
      </label>
      <Tooltip content={tooltip}>
        <RiInformationLine className="h-4 w-4 text-gray-400" />
      </Tooltip>
    </div>
    <div className="relative mt-1 flex rounded-md shadow-sm">
      {isDecimal && (
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <span className="text-gray-500 sm:text-sm">$</span>
        </div>
      )}
      <input
        id={id}
        type="text"
        inputMode={isDecimal ? "decimal" : "numeric"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 ${isDecimal ? 'pl-7' : ''}`}
        placeholder={placeholder}
      />
      <div className="absolute inset-y-0 right-0 flex -space-x-px">
        <button
          type="button"
          onClick={onIncrement}
          className="rounded-r-md border border-gray-300 bg-gray-50 px-2 text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700"
        >
          ▲
        </button>
        <button
          type="button"
          onClick={onDecrement}
          className="rounded-r-md border border-gray-300 bg-gray-50 px-2 text-gray-500 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700"
        >
          ▼
        </button>
      </div>
    </div>
  </div>
)

// Analytics configuration
const ANALYTICS_CONFIG: AnalyticsConfig = {
  enabled: true,
  category: 'calculators',
  action: 'email_capture',
  label: 'cpm_calculator_results_email_signup',
  elementId: 'cpm-calculator-email-form',
  pageLocation: 'CPM Calculator'
}

// CRM configuration
const CRM_CONFIG: CRMConfig = {
  tags: ['calculator_results', 'cpm'],
  source: 'cpm_calculator',
  customFields: {
    calculator_type: 'CPM',
    signup_location: 'calculator_results'
  },
  options: {
    addToList: true,
    updateExisting: true,
    retryOnFailure: true,
    retryAttempts: 3
  }
}

export function CPMCalculator(): JSX.Element {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    adSpend: 1000,
    impressions: 100000,
    clicks: undefined,
    conversions: undefined
  })

  const [displayValues, setDisplayValues] = useState({
    adSpend: '1,000.00',
    impressions: '100,000',
    clicks: '',
    conversions: ''
  })

  const [results, setResults] = useState<CalculatorResults | null>(null)
  const [email] = useState('')
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  // Debounced interact tracker (no input values; lean payload)
  const _debouncedInteract = useMemo(() => debounce((...args: unknown[]) => {
    const interactionType = (args[0] as string) ?? 'input_change'
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'cpm-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType }
    })
  }, 400), [getDeltaMs])

  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  const emailTemplate = useMemo(() => {
    // Base email template configuration
    const baseTemplate: EmailTemplateConfig = {
      templateAlias: 'welcome',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel: {
        base: {
          name: email ? email.split('@')[0]?.trim() ?? '' : '', // Set name from email and handle undefined
          logo_url: 'https://www.adsights.ai/logo-square.png',
          product_name: 'AdSights',
          product_url: 'https://www.adsights.ai',
          support_email: 'support@adsights.ai',
          sender_name: 'The AdSights Team',
          company_name: 'AdSights Inc.',
          company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702'
        },
        custom: {
          calculator_type: 'CPM',
          calculator_results: results ? {
            metrics: results,
            inputs: {
              adSpend: inputs.adSpend,
              impressions: inputs.impressions,
              clicks: inputs.clicks,
              conversions: inputs.conversions
            }
          } : null
        }
      }
    }

    return baseTemplate
  }, [email, results, inputs.adSpend, inputs.impressions, inputs.clicks, inputs.conversions])

  const handleInputChange = (field: keyof CalculatorInputs, value: string): void => {
    const isDecimal = field === 'adSpend'
    const formattedValue = formatNumberInput(value, isDecimal)
    
    setDisplayValues(prev => ({
      ...prev,
      [field]: formattedValue
    }))

    const numericValue = parseInputNumber(formattedValue)
    setInputs(prev => ({
      ...prev,
      [field]: numericValue || undefined
    }))
    trackInteract('input_change')
  }

  const handleStepChange = (field: keyof CalculatorInputs, increment: boolean, step: number): void => {
    const currentValue = inputs[field] ?? 0
    const newValue = Math.max(0, increment ? currentValue + step : currentValue - step)
    
    setInputs(prev => ({
      ...prev,
      [field]: newValue
    }))

    const isDecimal = field === 'adSpend'
    setDisplayValues(prev => ({
      ...prev,
      [field]: formatNumberInput(newValue.toString(), isDecimal)
    }))
    trackInteract('input_change')
  }

  const handleCalculate = (): void => {
    if (inputs.adSpend > 0 && inputs.impressions > 0) {
      const newResults = calculateMetrics(inputs)
      setResults(newResults)
      // Interact + Complete
      sendResourceEvent('interact', {
        resourceType: 'calculator',
        resourceSlug: 'cpm-calculator',
        engagementMs: getDeltaMs(),
        params: { interaction_type: 'calculate' }
      })
      sendResourceEvent('complete', {
        resourceType: 'calculator',
        resourceSlug: 'cpm-calculator',
        engagementMs: getTotalMs(),
        params: { }
      })
    }
  }

  const handleEmailSuccess = useCallback(() => {
    // Additional success handling if needed
  }, [])

  const formatDisplayNumber = (value: number | undefined, options: NumberFormatOptions = {}): string => {
    if (value === undefined) return ''
    return formatNumberWithOptions(value, options)
  }

  return (
    <div className="mx-auto max-w-4xl" role="main" aria-labelledby="calculator-heading">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Calculator Header */}
        <div className="px-8 pt-8">
          <h2 id="calculator-heading" className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            CPM Calculator
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Calculate your Cost Per Mille (CPM) and analyze campaign performance metrics
          </p>
        </div>

        {/* Calculator Form */}
        <div className="mt-8 grid gap-8 px-8 pb-8 sm:grid-cols-2">
          {/* Left Column - Required Metrics */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Required Metrics</h3>
            
            <NumberInput
              id="ad-spend"
              value={displayValues.adSpend}
              onChange={(value: string) => handleInputChange('adSpend', value)}
              isDecimal={true}
              placeholder="Enter amount"
              label="Total Ad Spend"
              tooltip="The total amount spent on your advertising campaign"
              onIncrement={() => handleStepChange('adSpend', true, 100)}
              onDecrement={() => handleStepChange('adSpend', false, 100)}
            />

            <NumberInput
              id="impressions"
              value={displayValues.impressions}
              onChange={(value: string) => handleInputChange('impressions', value)}
              placeholder="Enter impressions"
              label="Total Impressions"
              tooltip="The total number of times your ad was shown"
              onIncrement={() => handleStepChange('impressions', true, 1000)}
              onDecrement={() => handleStepChange('impressions', false, 1000)}
            />
          </div>

          {/* Right Column - Performance (Optional) */}
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Performance Metrics (Optional)
            </h3>
            
            <NumberInput
              id="clicks"
              value={displayValues.clicks}
              onChange={(value: string) => handleInputChange('clicks', value)}
              placeholder="Enter clicks"
              label="Total Clicks"
              tooltip="The total number of clicks received on your ad"
              onIncrement={() => handleStepChange('clicks', true, 100)}
              onDecrement={() => handleStepChange('clicks', false, 100)}
            />

            <NumberInput
              id="conversions"
              value={displayValues.conversions}
              onChange={(value: string) => handleInputChange('conversions', value)}
              placeholder="Enter conversions"
              label="Total Conversions"
              tooltip="The total number of conversions from your ad"
              onIncrement={() => handleStepChange('conversions', true, 10)}
              onDecrement={() => handleStepChange('conversions', false, 10)}
            />
          </div>
        </div>

        {/* Calculate Button */}
        <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={handleCalculate}
            className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            aria-label="Calculate CPM and performance metrics"
          >
            Calculate Metrics
          </button>
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8 space-y-8"
          >
            {/* Combined Results Card */}
            <div className="rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Campaign Performance Metrics
                </h3>
                <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
                  <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CPM</dt>
                    <dd className="mt-2 text-3xl font-semibold text-indigo-600 dark:text-indigo-400">
                      ${formatDisplayNumber(results.cpm, { decimals: 2 })}
                    </dd>
                  </div>
                  {results.ctr && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CTR</dt>
                      <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        {formatDisplayNumber(results.ctr, { decimals: 2 })}%
                      </dd>
                    </div>
                  )}
                  {results.cpc && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CPC</dt>
                      <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        ${formatDisplayNumber(results.cpc, { decimals: 2 })}
                      </dd>
                    </div>
                  )}
                  {results.cvr && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CVR</dt>
                      <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        {formatDisplayNumber(results.cvr, { decimals: 2 })}%
                      </dd>
                    </div>
                  )}
                  {results.cpa && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">CPA</dt>
                      <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        ${formatDisplayNumber(results.cpa, { decimals: 2 })}
                      </dd>
                    </div>
                  )}
                  {results.effectiveCPM && (
                    <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                      <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Effective CPM</dt>
                      <dd className="mt-2 text-3xl font-semibold text-gray-900 dark:text-white">
                        ${formatDisplayNumber(results.effectiveCPM, { decimals: 2 })}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Platform Benchmarks - Now in the same card */}
              <div className="pt-8 border-t border-gray-200 dark:border-gray-800">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
                  Platform Benchmarks
                </h3>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  {PLATFORM_BENCHMARKS.map((platform) => (
                    <div 
                      key={platform.platform}
                      className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <dt className="text-sm font-medium text-gray-900 dark:text-white">
                          {platform.platform}
                        </dt>
                        <Tooltip content={
                          <div>
                            <p>Source: {platform.source}</p>
                            <p className="text-xs mt-1">
                              <a 
                                href={platform.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-indigo-400 hover:text-indigo-300"
                              >
                                View Source →
                              </a>
                            </p>
                          </div>
                        }>
                          <RiInformationLine className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        </Tooltip>
                      </div>
                      <dd className="text-sm text-gray-600 dark:text-gray-400">
                        Average: ${formatDisplayNumber(platform.cpm.average, { decimals: 2 })}
                        <br />
                        Range: ${formatDisplayNumber(platform.cpm.low, { decimals: 2 })} - ${formatDisplayNumber(platform.cpm.high, { decimals: 2 })}
                      </dd>
                    </div>
                  ))}
                </div>

                <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                  <p>* Benchmarks are based on publicly available platform data and may vary based on targeting, seasonality, and market conditions. Click the info icon to view sources.</p>
                </div>
              </div>
            </div>

            {/* Embedded Email Capture */}
            <EmbeddedEmailCapture
              preset="inline"
              content={{
                title: {
                  text: "Get Your Detailed Analysis",
                  className: 'text-2xl font-semibold'
                },
                description: {
                  text: "Receive a comprehensive breakdown of your CPM metrics, benchmarks, and optimization recommendations.",
                  className: 'text-gray-600 dark:text-gray-400'
                },
                button: {
                  text: "Get Analysis",
                  loadingText: "Preparing Analysis..."
                },
                input: {
                  label: "Work Email",
                  placeholder: "Enter your work email"
                },
                messages: {
                  success: "Analysis sent!",
                  error: "There was an error sending your analysis. Please try again."
                }
              }}
              template={emailTemplate}
              analytics={ANALYTICS_CONFIG}
              crm={CRM_CONFIG}
              onSuccess={handleEmailSuccess}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAQ Section - always visible */}
      <div className="mt-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
          Frequently Asked Questions
        </h3>
        <FAQ 
          items={FAQ_ITEMS} 
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: "xl" }}
          className="w-full"
        />
      </div>
    </div>
  )
}
